import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FileText, Download, FileSpreadsheet, ShoppingBag, Store, Wallet } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountingDocuments() {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);
  const [invoices, setInvoices] = useState([]);
  const [years, setYears] = useState([]);
  const [sales, setSales] = useState([]);
  const [salesYears, setSalesYears] = useState([]);
  const [bizSales, setBizSales] = useState([]);
  const [bizYears, setBizYears] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/invoices`, { withCredentials: true });
        setInvoices(data.invoices || []); setYears(data.years || []);
      } catch (e) { /* not logged in or none */ }
      try {
        const { data } = await axios.get(`${API}/sales/invoices`, { withCredentials: true });
        setSales(data.sales || []); setSalesYears(data.years || []);
      } catch (e) { /* not a seller or none */ }
      try {
        const { data } = await axios.get(`${API}/business-sales/invoices`, { withCredentials: true });
        setBizSales(data.sales || []); setBizYears(data.years || []);
      } catch (e) { /* no business page or none */ }
      finally { setLoading(false); }
    })();
  }, []);

  const download = async (url, filename) => {
    try {
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) { alert(tr('Neizdevās lejuplādēt', 'Download failed', 'Не удалось скачать')); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('lv-LV') : '';

  const SummaryBar = ({ items, accent, label, kind }) => items.length === 0 ? null : (
    <div className={`flex flex-wrap items-center gap-2 mb-4 p-3 rounded-xl border ${accent === 'green' ? 'bg-[#FAFAFA] border-[#F3F2EE]' : 'bg-[#FFF6F0] border-[#F0D9C8]'}`} data-testid={`summary-bar-${kind}`}>
      <FileSpreadsheet className={`w-4 h-4 ${accent === 'green' ? 'text-[#4A6B53]' : 'text-[#D97736]'}`} />
      <span className="text-sm font-medium text-[#121212]">{label}:</span>
      {items.map((y) => (
        <Button key={y} type="button" variant="outline" size="sm"
          onClick={() => download(
            kind === 'purchases' ? `${API}/invoices/summary/${y}/pdf`
              : kind === 'shop' ? `${API}/sales/summary/${y}/pdf`
                : `${API}/business-sales/summary/${y}/pdf`,
            `kolv-kopsavilkums-${kind}-${y}.pdf`)}
          className={accent === 'green' ? 'border-[#E5E4E0] text-[#5C5C5C] hover:border-[#4A6B53]' : 'border-[#F0D9C8] text-[#D97736] hover:border-[#D97736]'}
          data-testid={`summary-${kind}-${y}`}>
          {y} <Download className="w-3.5 h-3.5 ml-1" />
        </Button>
      ))}
    </div>
  );

  const PurchasesTab = () => (
    <div data-testid="tab-purchases">
      <p className="text-sm text-[#5C5C5C] mb-4">{tr('Rēķini par taviem pirkumiem un gada kopsavilkums grāmatvedībai.', 'Invoices for your purchases and a yearly summary.', 'Счета по вашим покупкам и годовая сводка.')}</p>
      <SummaryBar items={years} accent="green" kind="purchases" label={tr('Gada kopsavilkums', 'Yearly summary', 'Годовая сводка')} />
      {invoices.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]" data-testid="no-invoices">{tr('Vēl nav apmaksātu pirkumu.', 'No paid purchases yet.', 'Пока нет оплаченных покупок.')}</p>
      ) : (
        <div className="space-y-2" data-testid="invoice-list">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center gap-3 p-3 border border-[#F3F2EE] rounded-xl hover:border-[#E5E4E0]" data-testid={`invoice-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#121212] truncate">{inv.description}</p>
                <p className="text-xs text-[#9CA3AF]">{fmtDate(inv.date)} · {inv.amount.toFixed(2)} {inv.currency}</p>
              </div>
              <Button type="button" variant="outline" size="sm"
                onClick={() => download(`${API}/invoices/${inv.id}/pdf`, `kolv-rekins-${inv.id.slice(-8)}.pdf`)}
                className="border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white shrink-0" data-testid={`download-invoice-${inv.id}`}>
                {tr('Rēķins', 'Invoice', 'Счёт')} <Download className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const SalesTab = ({ items, yearsList, kind, emptyText, descText, downloadBase, filePrefix }) => (
    <div data-testid={`tab-${kind}`}>
      <p className="text-sm text-[#5C5C5C] mb-4">{descText}</p>
      <SummaryBar items={yearsList} accent="orange" kind={kind} label={tr('Gada kopsavilkums', 'Yearly summary', 'Годовая сводка')} />
      {items.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]" data-testid={`no-${kind}`}>{emptyText}</p>
      ) : (
        <div className="space-y-2" data-testid={`${kind}-list`}>
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-3 border border-[#F3F2EE] rounded-xl hover:border-[#E5E4E0]" data-testid={`${kind}-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#121212] truncate">{s.description}</p>
                <p className="text-xs text-[#9CA3AF]">
                  {fmtDate(s.date)} · {tr('Bruto', 'Gross', 'Брутто')} {s.gross.toFixed(2)} · {tr('Komisija', 'Fee', 'Комиссия')} {s.commission.toFixed(2)} · {tr('Neto', 'Net', 'Нетто')} <span className="text-[#4A6B53] font-medium">{s.net.toFixed(2)} {s.currency}</span>
                </p>
              </div>
              <Button type="button" variant="outline" size="sm"
                onClick={() => download(`${downloadBase}/${s.id}/pdf`, `${filePrefix}-${s.id.slice(-8)}.pdf`)}
                className="border-[#D97736] text-[#D97736] hover:bg-[#D97736] hover:text-white shrink-0" data-testid={`download-${kind}-${s.id}`}>
                {tr('Pārskats', 'Statement', 'Отчёт')} <Download className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const defaultTab = sales.length > 0 ? 'shop' : bizSales.length > 0 ? 'business' : 'purchases';

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6" data-testid="accounting-documents">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-[#4A6B53]" />
        <h3 className="font-outfit font-semibold text-lg text-[#121212]">{tr('Grāmatvedība', 'Accounting', 'Бухгалтерия')}</h3>
      </div>

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">{tr('Ielādē…', 'Loading…', 'Загрузка…')}</p>
      ) : (
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="mb-5 bg-[#F3F2EE]" data-testid="accounting-tabs">
            <TabsTrigger value="shop" data-testid="tab-trigger-shop" className="data-[state=active]:bg-white">
              <Store className="w-4 h-4 mr-1.5" /> {tr('Veikals', 'Shop', 'Магазин')}
            </TabsTrigger>
            <TabsTrigger value="business" data-testid="tab-trigger-business" className="data-[state=active]:bg-white">
              <Wallet className="w-4 h-4 mr-1.5" /> {tr('Biznesa lapa', 'Business page', 'Бизнес-страница')}
            </TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-trigger-purchases" className="data-[state=active]:bg-white">
              <ShoppingBag className="w-4 h-4 mr-1.5" /> {tr('Mani pirkumi', 'My purchases', 'Мои покупки')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="shop">
            <SalesTab
              items={sales} yearsList={salesYears} kind="shop"
              descText={tr('Veikala pārdošanas pārskati (bruto, komisija, izmaksātā summa) un gada kopsavilkums.', 'Shop sales statements (gross, commission, payout) and a yearly summary.', 'Отчёты о продажах магазина (брутто, комиссия, выплата) и годовая сводка.')}
              emptyText={tr('Vēl nav veikala pārdošanas.', 'No shop sales yet.', 'Пока нет продаж магазина.')}
              downloadBase={`${API}/sales/invoices`} filePrefix="kolv-pardosana" />
          </TabsContent>

          <TabsContent value="business">
            <SalesTab
              items={bizSales} yearsList={bizYears} kind="business"
              descText={tr('Biznesa lapas rezervāciju ieņēmumi (bruto, komisija, izmaksātā summa) un gada kopsavilkums.', 'Business-page reservation income (gross, commission, payout) and a yearly summary.', 'Доход бизнес-страницы от бронирований (брутто, комиссия, выплата) и годовая сводка.')}
              emptyText={tr('Vēl nav apmaksātu rezervāciju. Ieslēdz online maksājumus biznesa lapas rediģēšanā.', 'No paid reservations yet. Enable online payments in your business-page editor.', 'Пока нет оплаченных бронирований. Включите онлайн-оплату в редакторе бизнес-страницы.')}
              downloadBase={`${API}/business-sales/invoices`} filePrefix="kolv-rezervacija" />
          </TabsContent>

          <TabsContent value="purchases">
            <PurchasesTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
